// Cues (SPEC F6 Chop preview, §8): up to 16 timestamps on a record, one per pad, set from taps only. The `cues` table is
// Dexie v5, keyed [videoId+pad]. A cue is a seek target for the player: no audio is read, rendered or stored.

import type { Cue } from '../../data/types';
import { getDb } from '../schema';

export const PAD_COUNT = 16;

const round = (s: number): number => Math.round(s * 1000) / 1000;
const clamp = (s: number, durationS: number | undefined): number => Math.min(durationS !== undefined && durationS > 0 ? durationS : Infinity, Math.max(0, s));
const checkPad = (pad: number): void => {
  if (!Number.isInteger(pad) || pad < 0 || pad >= PAD_COUNT) throw new RangeError(`pad ${pad} is not 0..${PAD_COUNT - 1}`);
};

/** A record's cues, by pad (A01 first). */
export async function listCues(videoId: string): Promise<Cue[]> {
  const rows = await getDb().cues.where('videoId').equals(videoId).toArray();
  return rows.sort((a, b) => a.pad - b.pad);
}

/** Sets the pad's cue (replacing the one there), kept between 0 and the record's duration when it is known. */
export async function setCue(videoId: string, pad: number, atS: number, durationS?: number): Promise<Cue> {
  checkPad(pad);
  const cue: Cue = { videoId, pad, atS: round(clamp(atS, durationS)) };
  await getDb().cues.put(cue);
  return cue;
}

/** Moves a set cue by `deltaS`, kept between 0 and the duration. Returns the new cue, or undefined when the pad is empty. */
export async function nudgeCue(videoId: string, pad: number, deltaS: number, durationS?: number): Promise<Cue | undefined> {
  checkPad(pad);
  const db = getDb();
  return db.transaction('rw', db.cues, async () => {
    const cur = await db.cues.get([videoId, pad] as never);
    if (!cur) return undefined;
    const next: Cue = { ...cur, atS: round(clamp(cur.atS + deltaS, durationS)) };
    await db.cues.put(next);
    return next;
  });
}

/** Clears one pad. Returns the cue it held, so Undo can put it back. */
export async function clearCue(videoId: string, pad: number): Promise<Cue | undefined> {
  checkPad(pad);
  const db = getDb();
  return db.transaction('rw', db.cues, async () => {
    const cur = await db.cues.get([videoId, pad] as never);
    if (cur) await db.cues.delete([videoId, pad] as never);
    return cur;
  });
}

/** Clears every cue on a record. Returns what was there, for Undo. */
export async function clearAllCues(videoId: string): Promise<Cue[]> {
  const db = getDb();
  return db.transaction('rw', db.cues, async () => {
    const rows = await db.cues.where('videoId').equals(videoId).toArray();
    await db.cues.bulkDelete(rows.map((c) => [c.videoId, c.pad] as never));
    return rows;
  });
}

/** Undo: puts cues back exactly as they were. */
export async function restoreCues(cues: readonly Cue[]): Promise<void> {
  if (cues.length) await getDb().cues.bulkPut([...cues]);
}
